import React, { useState } from "react";
import { ZoomIn } from "lucide-react";
import { cn } from "src/lib/utils";

/*
 * Zoom of the dashboard charts to a date range.
 * - Charts open zoomed to the last DEFAULT_ZOOM_DAYS days when the period is longer than that.
 * - Preset buttons (24 h / 7 days / 30 days / All), only those shorter than the period.
 * - Dragging over the chart zooms to the selected range ("custom"). Charts using it set
 *   data-swapy-no-drag on the plot, so the drag does not move the dashboard widget.
 */

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

export const DEFAULT_ZOOM_DAYS = 7;

const PRESETS = [
  { key: "24h", label: "24 h", ms: DAY },
  { key: "7d", label: "7 days", ms: 7 * DAY },
  { key: "30d", label: "30 days", ms: 30 * DAY },
];

/*
 * Presets that make sense for a period of spanMs (minMs: the smallest range worth showing,
 * e.g. 1 day for charts with one point per day). "all" is always available.
 */
export function zoomPresets(spanMs, { minMs = 0 } = {}) {
  const presets = PRESETS.filter((p) => p.ms < spanMs && p.ms >= minMs);
  return [...presets, { key: "all", label: "All" }];
}

/* Initial preset: the last DEFAULT_ZOOM_DAYS days if the period is longer, everything otherwise */
export function defaultZoomPreset(spanMs) {
  return spanMs > DEFAULT_ZOOM_DAYS * DAY ? `${DEFAULT_ZOOM_DAYS}d` : "all";
}

/* [from, to] visible for a zoom state, inside the full range [fullFrom, fullTo] */
export function zoomWindow(zoom, fullFrom, fullTo) {
  if (!zoom || zoom.preset === "all") return [fullFrom, fullTo];
  if (zoom.preset === "custom") return [Math.max(zoom.from, fullFrom), Math.min(zoom.to, fullTo)];
  const preset = PRESETS.find((p) => p.key === zoom.preset);
  return preset ? [Math.max(fullTo - preset.ms, fullFrom), fullTo] : [fullFrom, fullTo];
}

/*
 * Drag-to-zoom on a recharts chart. getValue(event) returns the x value under the pointer
 * (timestamp) or null. onZoom(from, to) is called when the drag covers more than minSpan.
 * Spread `handlers` on the chart and draw a <ReferenceArea x1={drag.start} x2={drag.end} />
 * while `drag` is set.
 */
export function useDragZoom({ getValue, onZoom, minSpan = 0 }) {
  const [drag, setDrag] = useState(null);

  const handlers = {
    onMouseDown: (event) => {
      const value = getValue(event);
      if (value !== null && value !== undefined) setDrag({ start: value, end: value });
    },
    onMouseMove: (event) => {
      if (!drag) return;
      const value = getValue(event);
      if (value !== null && value !== undefined) setDrag((d) => d && { ...d, end: value });
    },
    onMouseUp: () => {
      if (drag) {
        const from = Math.min(drag.start, drag.end);
        const to = Math.max(drag.start, drag.end);
        if (to - from > minSpan) onZoom(from, to);
      }
      setDrag(null);
    },
    onMouseLeave: () => setDrag(null),
  };

  return { drag, handlers };
}

/* "2025-03-07..." -> local midnight of that day (ms), or NaN */
export function dayStart(text) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(text ?? ""));
  return match ? new Date(+match[1], +match[2] - 1, +match[3]).getTime() : NaN;
}

/* "07/03" label of a day, falls back to the "DD" part of the timestamp */
export function dayLabel(ts, text) {
  if (!Number.isFinite(ts)) return String(text ?? "").substring(8, 10) || "DD";
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/*
 * Zoom for charts with one point per day on a category axis (audio dashboard).
 *   data: [{ ts, ... }] sorted by time, ts = start of the day (NaN disables the zoom)
 * Returns the visible points, the props for ChartZoomControls, the drag handlers for the chart
 * and `dragArea` ({ start, end } points of visibleData, or null) for a ReferenceArea.
 */
export function useDailyChartZoom(data) {
  const valid = data.length > 0 && data.every((d) => Number.isFinite(d.ts));
  const fullFrom = valid ? data[0].ts : 0;
  const fullTo = valid ? data[data.length - 1].ts + DAY : 0; // End of the last day (exclusive)
  const span = fullTo - fullFrom;

  const presets = React.useMemo(() => (valid ? zoomPresets(span, { minMs: 7 * DAY }) : []), [valid, span]);
  const [zoom, setZoom] = useState(() => ({ preset: defaultZoomPreset(span) }));
  React.useEffect(() => {
    setZoom({ preset: defaultZoomPreset(span) }); // New period: back to the default zoom
  }, [span]);

  const [from, to] = zoomWindow(zoom, fullFrom, fullTo);
  const visibleData = React.useMemo(
    () => (valid ? data.filter((d) => d.ts >= from && d.ts < to) : data),
    [valid, data, from, to]
  );

  const { drag, handlers } = useDragZoom({
    getValue: (event) => {
      const index = event?.activeTooltipIndex;
      return Number.isInteger(index) && visibleData[index] ? visibleData[index].ts : null;
    },
    onZoom: (start, end) => setZoom({ preset: "custom", from: start, to: end + DAY }),
    minSpan: 0, // At least two days
  });

  const pointAt = (ts) => visibleData.find((d) => d.ts === ts);
  const dragArea = drag && valid ? { start: pointAt(drag.start), end: pointAt(drag.end) } : null;

  return {
    visibleData,
    zoomed: valid && zoom.preset !== "all",
    controls: { presets, value: zoom.preset, onChange: (preset) => setZoom({ preset }) },
    dragHandlers: valid ? handlers : {},
    dragArea,
  };
}

/* Preset buttons + hint, shown above the chart */
export function ChartZoomControls({ presets, value, onChange, className }) {
  if (!presets || presets.length <= 1) return null;

  return (
    <div className={cn("chart-zoom-controls", className)} data-swapy-no-drag>
      <div className="chart-zoom-presets" role="group" aria-label="Zoom to a date range">
        {presets.map((preset) => (
          <button
            key={preset.key}
            type="button"
            className={cn("chart-zoom-preset", value === preset.key && "is-active")}
            aria-pressed={value === preset.key}
            onClick={() => onChange(preset.key)}
          >
            {preset.label}
          </button>
        ))}
        {value === "custom" && (
          <span className="chart-zoom-preset is-active" aria-current="true">
            Custom
          </span>
        )}
      </div>
      <span className="chart-zoom-hint">
        <ZoomIn className="h-3.5 w-3.5" aria-hidden="true" />
        Drag over the chart to zoom
      </span>
    </div>
  );
}
